import React from 'react';

import styled from 'styled-components';

// constants
import aampeTheme from '../../../constants/aampeTheme';

// models
import { AampeStepperStepProps } from '../../../models/stepper.model';

import AampeTooltip from '../AampeTooltip/AampeTooltip';
// components
import Divider, { DividerType } from '../Divider';
import AampeStepCircle from './AampeStepCircle';

interface StyledProps {
    color?: string;
    backgroundColor?: string;
}

export type StepProps = Omit<AampeStepperStepProps, 'renderComponent'>;

interface AampeStepsProps {
    currentStepIndex: number;
    steps: StepProps[];
    onClick: (index: number) => void;
    showStepsAsCompleted?: boolean;
    className?: string;
    dividerType?: DividerType;
    stepsStyle?: React.CSSProperties;
    dividerWidth?: number | string;
}

const AampeSteps: React.FC<React.PropsWithChildren<AampeStepsProps>> = ({
    steps,
    currentStepIndex,
    onClick,
    showStepsAsCompleted,
    className,
    dividerType = 'horizontal',
    stepsStyle,
    dividerWidth,
}) => {
    return (
        <Container className={className} style={stepsStyle}>
            {(steps || []).map((step, index) => {
                const isCompleted =
                    (index < currentStepIndex || showStepsAsCompleted) &&
                    ((step.getCompletionStatus && step.getCompletionStatus()) ||
                        !step?.getCompletionStatus);
                return (
                    <React.Fragment key={index}>
                        <AampeTooltip
                            title={
                                step?.disableStepInfo?.isDisabled
                                    ? step?.disableStepInfo?.tooltipText
                                    : ''
                            }
                            placement="top"
                        >
                            <Step onClick={() => onClick(index)}>
                                <AampeStepCircle
                                    index={index}
                                    currentStepIndex={currentStepIndex}
                                    isCompleted={isCompleted}
                                />
                                <Text
                                    color={
                                        index === currentStepIndex
                                            ? aampeTheme.colors
                                                  .textAccessibleWG01
                                            : aampeTheme.colors
                                                  .textAccessibleWG01
                                    }
                                >
                                    {step.title}
                                </Text>
                            </Step>
                        </AampeTooltip>
                        {index !== steps.length - 1 && (
                            <StyledDivider
                                type={dividerType}
                                dividerWidth={dividerWidth}
                                color={
                                    isCompleted
                                        ? aampeTheme.colors.success500
                                        : aampeTheme.colors.bordersWG01
                                }
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </Container>
    );
};

export default AampeSteps;

const Container = styled.div({
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    gap: aampeTheme.spacing(2),
    padding: aampeTheme.spacing4(0, 6),
});

const Step = styled.div({
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'start',
    gap: aampeTheme.spacing4(2),
    cursor: 'pointer',
});

const Text = styled.span(({ color }: StyledProps) => ({
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.medium,
    lineHeight: '20px',
    color: color,
}));

interface DividerStyledProps {
    dividerWidth?: number | string;
}

const StyledDivider = styled(Divider)<DividerStyledProps>(
    ({ dividerWidth }) => ({
        flex: dividerWidth ? '0 0 auto' : 1,
        width:
            dividerWidth !== undefined
                ? typeof dividerWidth === 'number'
                    ? `${dividerWidth}px`
                    : dividerWidth
                : 'auto',
    }),
);
